import type {SiteLite} from '@/multisite/types'

const trimSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, '')

const looksLikeSitePathPrefix = (value: string): boolean => /^[a-z]{2}(?:-[a-z]{2})?$/i.test(value)

const normalizeSegment = (value: string | null | undefined, fallback: string): string => {
  const trimmed = trimSlashes(value || '')
  return trimmed || fallback
}

const joinSitePath = (
  siteBasePath: string,
  ...segments: Array<string | null | undefined>
): string => {
  const parts = [
    trimSlashes(siteBasePath),
    ...segments.map((segment) => trimSlashes(segment || '')),
  ].filter(Boolean)
  return `/${parts.join('/')}`
}

const relativeCategorySlugForSite = (site: SiteLite, slug: string): string => {
  let normalized = trimSlashes(slug)
  const siteBasePath = trimSlashes(site.routing.basePath)
  const blogBasePath = blogBasePathForSite(site)

  if (siteBasePath && normalized === siteBasePath) {
    normalized = ''
  } else if (siteBasePath && normalized.startsWith(`${siteBasePath}/`)) {
    normalized = normalized.slice(siteBasePath.length + 1)
  }

  const [firstSegment, ...remainingSegments] = normalized.split('/')
  if (
    remainingSegments.length > 0 &&
    firstSegment &&
    firstSegment !== blogBasePath &&
    looksLikeSitePathPrefix(firstSegment)
  ) {
    normalized = remainingSegments.join('/')
  }

  if (normalized === blogBasePath) {
    return ''
  }

  if (normalized.startsWith(`${blogBasePath}/`)) {
    return normalized.slice(blogBasePath.length + 1)
  }

  return normalized
}

const blogBasePathForSite = (site: SiteLite): string =>
  normalizeSegment(site.basePages?.blog?.basePath, 'blog')

const productsBasePathForSite = (site: SiteLite): string =>
  normalizeSegment(site.basePages?.products?.basePath, 'products')

const storesBasePathForSite = (site: SiteLite): string =>
  normalizeSegment(site.basePages?.dealers?.basePath, 'dealers')

const campaignsBasePathForSite = (site: SiteLite): string =>
  normalizeSegment(site.basePages?.campaigns?.basePath, 'campaigns')

export const blogIndexPath = (site: SiteLite): string =>
  joinSitePath(site.routing.basePath, blogBasePathForSite(site))

export const articlePath = (site: SiteLite, slug: string): string =>
  joinSitePath(site.routing.basePath, blogBasePathForSite(site), slug)

export const articleCategoryPath = (site: SiteLite, slug: string): string =>
  joinSitePath(
    site.routing.basePath,
    blogBasePathForSite(site),
    relativeCategorySlugForSite(site, slug),
  )

export const productsIndexPath = (site: SiteLite): string =>
  joinSitePath(site.routing.basePath, productsBasePathForSite(site))

export const productPath = (site: SiteLite, slugOrTaxonomy: string): string =>
  joinSitePath(site.routing.basePath, productsBasePathForSite(site), slugOrTaxonomy)

export const storesIndexPath = (site: SiteLite): string =>
  joinSitePath(site.routing.basePath, storesBasePathForSite(site))

export const storePath = (site: SiteLite, slug: string): string =>
  joinSitePath(site.routing.basePath, storesBasePathForSite(site), slug)

export const campaignPath = (site: SiteLite, slug: string): string => {
  const normalizedSlug = trimSlashes(slug)
  const siteBasePath = trimSlashes(site.routing.basePath)
  const campaignsBasePath = campaignsBasePathForSite(site)

  if (siteBasePath && normalizedSlug.startsWith(`${siteBasePath}/`)) {
    return `/${normalizedSlug}`
  }
  if (normalizedSlug === campaignsBasePath || normalizedSlug.startsWith(`${campaignsBasePath}/`)) {
    return joinSitePath(site.routing.basePath, normalizedSlug)
  }
  return joinSitePath(site.routing.basePath, campaignsBasePath, normalizedSlug)
}
